import SelectBuscable from '@/shared/components/SelectBuscable'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useMemo } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { mockEvaluations } from '@/mocks/evaluaciones.mock';
import DatosDeEjemploBanner from '@/shared/components/DatosDeEjemploBanner';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

const CalendarFolded = ({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);


export const SupplierEvaluations: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const formatDateDDMMYYYY = (isoDate: string) => {
    const [year, month, day] = isoDate.split("-");
    if (!year || !month || !day) return "";
    return `${day}/${month}/${year}`;
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
  };

  // Filtro sobre la lista. El "Periodo evaluado" es MM/AAAA (granularidad de
  // mes), así que el rango de fechas se compara a nivel YYYY-MM: la porción
  // año-mes del periodo debe caer dentro de [fromDate, toDate].
  const filteredEvaluations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const periodToYM = (p: string) => {
      const [m, y] = (p || "").split("/");
      return y && m ? `${y}-${m.padStart(2, "0")}` : "";
    };
    const fromYM = fromDate ? fromDate.slice(0, 7) : "";
    const toYM = toDate ? toDate.slice(0, 7) : "";

    return mockEvaluations.filter((e) => {
      const matchesSearch =
        !term ||
        e.supplier.toLowerCase().includes(term) ||
        e.rfc.toLowerCase().includes(term) ||
        e.project.toLowerCase().includes(term);
      const ym = periodToYM(e.evaluatedPeriod);
      const matchesFrom = !fromYM || (ym !== "" && ym >= fromYM);
      const matchesTo = !toYM || (ym !== "" && ym <= toYM);
      return matchesSearch && matchesFrom && matchesTo;
    });
  }, [searchTerm, fromDate, toDate]);

  if (
    userHasPermission(
      "proveedores.fiscal.validaciones.consultar",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <DatosDeEjemploBanner detalle="El módulo de evaluaciones todavía no está conectado al servidor: estos proveedores y calificaciones son de muestra." />
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <TituloDePantalla className="mb-2">
            Evaluación y calificación de proveedores
          </TituloDePantalla>
          <p className="text-gray-600 dark:text-gray-400">
            Administra aquí tu lista de solicitudes de personal
          </p>
        </div>
        <button
          onClick={() => navigate("/rh/evaluations/new")}
          className={estiloDeBoton('primario')}
        >
          <Plus className="w-4 h-4" />
          Nueva evaluación
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-[#E0E0E0] p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar en solicitud de vacantes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 !border !border-[#E5E7EB] rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
            />
          </div>

          {/* Estado Dropdown */}
          <div className="relative">
            <SelectBuscable
              variante="listado"
              ancho="contenido"
              etiqueta="Estado"
              todos="Todos los estados"
              value={statusFilter}
              onChange={setStatusFilter}
              placeholderDeBusqueda="Buscar estado…"
              canDelete={false}
              opciones={[]}
            />
          </div>

          {/* Date From */}
          <div className="relative w-[150px] h-[38px] border border-[#E5E7EB] rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#3B5998] focus-within:border-transparent">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
              {fromDate ? (
                <span className="text-gray-900 dark:text-white">
                  {formatDateDDMMYYYY(fromDate)}
                </span>
              ) : (
                <span className="text-gray-400">dd/mm/aaaa</span>
              )}
            </span>
            <CalendarFolded className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) =>
                (
                  e.currentTarget as HTMLInputElement & {
                    showPicker?: () => void;
                  }
                ).showPicker?.()
              }
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>

          {/* Date To */}
          <div className="relative w-[150px] h-[38px] border border-[#E5E7EB] rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#3B5998] focus-within:border-transparent">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
              {toDate ? (
                <span className="text-gray-900 dark:text-white">
                  {formatDateDDMMYYYY(toDate)}
                </span>
              ) : (
                <span className="text-gray-400">dd/mm/aaaa</span>
              )}
            </span>
            <CalendarFolded className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) =>
                (
                  e.currentTarget as HTMLInputElement & {
                    showPicker?: () => void;
                  }
                ).showPicker?.()
              }
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>

          {/* Clear Button */}
          <button
            onClick={handleClearFilters}
            className="text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Evaluations Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Proveedor
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  RFC o razón social
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Periodo evaluado
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Proyecto
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Calificación final
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {filteredEvaluations.map((evaluation) => (
                <tr
                  key={evaluation.id}
                  onClick={() => navigate(`/rh/evaluations/${evaluation.id}`)}
                  className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {evaluation.supplier}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {evaluation.rfc}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {evaluation.evaluatedPeriod}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {evaluation.project}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {evaluation.finalRating}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
